import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Card, CardContent, Chip, TextField, InputAdornment, Paper, Button } from '@mui/material';
import {
  Search, MailOutline, Settings, Security,
  Dns, SwapHoriz, People, BugReport,
  QuestionAnswer, MenuBook, ContactSupport, MonitorHeart,
  Article, ArrowForward,
} from '@mui/icons-material';
import PublicLayout from '../components/layout/PublicLayout';

const categories = [
  { icon: <MailOutline sx={{ fontSize: 32, color: '#dc2626' }} />, title: 'Getting Started', count: 12, desc: 'Account setup, first login, and initial configuration guides.' },
  { icon: <Settings sx={{ fontSize: 32, color: '#dc2626' }} />, title: 'Account & Settings', count: 18, desc: 'Profile, signatures, filters, forwarding, and auto-reply setup.' },
  { icon: <Security sx={{ fontSize: 32, color: '#dc2626' }} />, title: 'Security & Privacy', count: 15, desc: 'Two-factor auth, password policies, and data protection.' },
  { icon: <Dns sx={{ fontSize: 32, color: '#dc2626' }} />, title: 'Domain & DNS', count: 10, desc: 'Domain verification, MX records, SPF/DKIM/DMARC setup.' },
  { icon: <SwapHoriz sx={{ fontSize: 32, color: '#dc2626' }} />, title: 'Migration', count: 8, desc: 'Email migration from Gmail, Outlook, Zoho, and IMAP providers.' },
  { icon: <People sx={{ fontSize: 32, color: '#dc2626' }} />, title: 'Admin & Users', count: 14, desc: 'User management, roles, quotas, and organization settings.' },
  { icon: <BugReport sx={{ fontSize: 32, color: '#dc2626' }} />, title: 'Troubleshooting', count: 20, desc: 'Common issues, error messages, and diagnostic steps.' },
  { icon: <QuestionAnswer sx={{ fontSize: 32, color: '#dc2626' }} />, title: 'FAQ', count: 25, desc: 'Frequently asked questions about billing, features, and limits.' },
];

const resources = [
  { icon: <MenuBook sx={{ fontSize: 28, color: '#dc2626' }} />, title: 'Documentation', desc: 'Browse all how-to guides, setup articles, and reference docs.', path: '/documentation', cta: 'Open Docs' },
  { icon: <ContactSupport sx={{ fontSize: 28, color: '#dc2626' }} />, title: 'Contact Support', desc: 'Submit a support request and our team will respond within hours.', path: '/contact', cta: 'Get Help' },
  { icon: <MonitorHeart sx={{ fontSize: 28, color: '#dc2626' }} />, title: 'System Status', desc: 'Check live uptime, incidents, and service health in real time.', path: '/status', cta: 'View Status' },
];

// Searchable documentation articles mapping to documentation page sections
const searchArticles = [
  { title: 'Getting Started with CruvzMail', section: 'getting-started', keywords: 'sign in login first time setup account create welcome interface overview sidebar navigation' },
  { title: 'Composing & Sending Email', section: 'composing-email', keywords: 'compose send write new email rich text formatting attachments cc bcc reply forward' },
  { title: 'Organization & Folders', section: 'organization', keywords: 'folders labels organize inbox sent drafts spam trash archive move messages filter' },
  { title: 'Security & Two-Factor Authentication', section: 'security', keywords: 'security 2fa two factor authentication password policies data protection encryption' },
  { title: 'Email Client Setup - Outlook', section: 'email-client', keywords: 'outlook client setup imap smtp pop3 desktop email client configuration thunderbird apple mail' },
  { title: 'Email Client Setup - Thunderbird', section: 'email-client', keywords: 'thunderbird mozilla email client setup imap smtp configuration' },
  { title: 'Email Client Setup - Apple Mail', section: 'email-client', keywords: 'apple mail macos ios iphone ipad email client setup' },
  { title: 'Mobile App Setup - Android', section: 'email-client', keywords: 'android phone mobile gmail app email setup configuration' },
  { title: 'Mobile App Setup - iOS', section: 'email-client', keywords: 'ios iphone ipad mobile apple mail setup configuration' },
  { title: 'DNS & Domain Setup', section: 'dns-domain', keywords: 'dns domain mx records spf dkim dmarc verification domain setup custom domain' },
  { title: 'Admin Panel Guide', section: 'admin-panel', keywords: 'admin panel dashboard users management roles permissions organization settings quota' },
  { title: 'Productivity Features', section: 'productivity', keywords: 'calendar tasks notes contacts bookmarks ai assistant productivity features' },
  { title: 'Keyboard Shortcuts', section: 'shortcuts', keywords: 'keyboard shortcuts hotkeys quick actions compose reply forward delete archive' },
  { title: 'Configuration & Limits', section: 'configuration', keywords: 'configuration limits attachment size mailbox quota storage plan settings' },
  { title: 'Email Signatures Setup', section: 'composing-email', keywords: 'signature email signature html create edit default' },
  { title: 'Auto-Reply & Vacation Responder', section: 'organization', keywords: 'auto reply vacation responder out of office automatic response' },
  { title: 'Import & Export Emails', section: 'organization', keywords: 'import export backup emails migrate transfer eml mbox' },
  { title: 'Contact Management', section: 'productivity', keywords: 'contacts address book groups import export vcard csv' },
  { title: 'Calendar & Events', section: 'productivity', keywords: 'calendar events meetings schedule reminder invite' },
  { title: 'Spam & Junk Mail Settings', section: 'security', keywords: 'spam junk filter blocklist allowlist quarantine rspamd' },
  { title: 'Email Forwarding Rules', section: 'organization', keywords: 'forwarding rules redirect email automatic forward' },
  { title: 'Custom Branding', section: 'admin-panel', keywords: 'branding logo theme colors custom domain whitelabel' },
  { title: 'Billing & Plans', section: 'configuration', keywords: 'billing plans pricing subscription upgrade downgrade payment invoice' },
  { title: 'Team Chat Setup', section: 'productivity', keywords: 'chat team messaging group channels instant messaging' },
  { title: 'Helpdesk & Ticketing', section: 'productivity', keywords: 'helpdesk ticketing support tickets customer service' },
];

const categoryToSection: Record<string, string> = {
  'Getting Started': 'getting-started',
  'Account & Settings': 'composing-email',
  'Security & Privacy': 'security',
  'Domain & DNS': 'dns-domain',
  'Migration': 'getting-started',
  'Admin & Users': 'admin-panel',
  'Troubleshooting': 'configuration',
  'FAQ': 'getting-started',
};

const HelpCenterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const filteredArticles = searchQuery.trim().length >= 2
    ? searchArticles.filter((a) => {
        const q = searchQuery.toLowerCase();
        return a.title.toLowerCase().includes(q) || a.keywords.toLowerCase().includes(q);
      }).slice(0, 8)
    : [];

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (filteredArticles.length > 0) {
        navigate(`/documentation?section=${filteredArticles[0].section}`);
        setSearchQuery('');
        setIsFocused(false);
      } else if (searchQuery.trim().length >= 2) {
        navigate('/documentation');
        setSearchQuery('');
        setIsFocused(false);
      }
    } else if (e.key === 'Escape') {
      setSearchQuery('');
      setIsFocused(false);
    }
  };

  return (
    <PublicLayout activeNav="Support">

      {/* â”€â”€ HERO â”€â”€ */}
      <Box sx={{
        pt: { xs: '120px', md: '145px' }, pb: { xs: 10, md: 14 }, px: { xs: 2, md: 5 },
        position: 'relative', overflow: 'hidden', textAlign: 'center',
        background: 'linear-gradient(160deg, #0f172a 0%, #1e1b4b 50%, #1e0a0a 100%)',
      }}>
        {/* Glowing orbs */}
        <Box sx={{ position: 'absolute', top: -150, right: -150, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(220,38,38,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -100, left: -100, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <Box sx={{ maxWidth: 820, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Chip label="Support Center" sx={{ mb: 3, bgcolor: 'rgba(220,38,38,0.15)', color: '#fca5a5', fontWeight: 700, fontSize: '0.78rem', border: '1px solid rgba(220,38,38,0.3)', letterSpacing: '0.06em' }} />
          <Typography variant="h1" sx={{
            fontWeight: 900, fontSize: { xs: '2.4rem', md: '3.8rem' }, lineHeight: 1.1, mb: 3,
            background: 'linear-gradient(135deg, #ffffff 30%, #fca5a5 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            How Can We Help?
          </Typography>
          <Typography sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, color: 'rgba(255,255,255,0.6)', maxWidth: 560, mx: 'auto', lineHeight: 1.85, mb: 5 }}>
            Search our knowledge base, browse guides by category, or reach our support team directly.
          </Typography>

          {/* Search Bar */}
          <Box sx={{ position: 'relative', maxWidth: 620, mx: 'auto' }}>
            <TextField
              fullWidth
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setTimeout(() => setIsFocused(false), 250)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search â€” e.g. Outlook setup, DKIM, 2FA, calendar..."
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ color: '#94a3b8' }} /></InputAdornment>,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: '#fff',
                  borderRadius: filteredArticles.length > 0 && isFocused ? '14px 14px 0 0' : '14px',
                  fontSize: '1rem',
                  boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
                  '& fieldset': { border: 'none' },
                },
              }}
            />
            {filteredArticles.length > 0 && isFocused && (
              <Paper elevation={8} sx={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                borderRadius: '0 0 14px 14px', overflow: 'hidden',
                border: '1px solid #e2e8f0', borderTop: '1px solid #f1f5f9',
              }}>
                {filteredArticles.map((article) => (
                  <Box
                    key={article.title + article.section}
                    onClick={() => { navigate(`/documentation?section=${article.section}`); setSearchQuery(''); setIsFocused(false); }}
                    sx={{
                      px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5,
                      cursor: 'pointer', transition: 'background 0.15s',
                      '&:hover': { bgcolor: '#fef2f2' },
                      borderBottom: '1px solid #f8fafc',
                    }}
                  >
                    <Article sx={{ fontSize: 18, color: '#dc2626', flexShrink: 0 }} />
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography sx={{ fontSize: '0.88rem', fontWeight: 600, color: '#0f172a' }}>{article.title}</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8' }}>Documentation â€º {article.section.replace(/-/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}</Typography>
                    </Box>
                  </Box>
                ))}
                <Box
                  onClick={() => { navigate('/documentation'); setSearchQuery(''); setIsFocused(false); }}
                  sx={{ px: 2.5, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, cursor: 'pointer', bgcolor: '#f8fafc', '&:hover': { bgcolor: '#f1f5f9' } }}
                >
                  <Typography sx={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>View all documentation</Typography>
                  <ArrowForward sx={{ fontSize: 13, color: '#94a3b8' }} />
                </Box>
              </Paper>
            )}
            {searchQuery.trim().length >= 2 && filteredArticles.length === 0 && isFocused && (
              <Paper elevation={8} sx={{
                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                borderRadius: '0 0 14px 14px', p: 3, textAlign: 'center',
                border: '1px solid #e2e8f0',
              }}>
                <Typography sx={{ fontSize: '0.9rem', color: '#64748b' }}>No articles found for "{searchQuery}"</Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8', mt: 0.5 }}>Try different keywords or{' '}
                  <Box component="span" sx={{ color: '#dc2626', cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate('/documentation')}>browse all documentation</Box>
                </Typography>
              </Paper>
            )}
          </Box>

          {/* Popular quick-links */}
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap', mt: 3 }}>
            {['Getting Started', 'DKIM Setup', '2FA', 'Outlook Setup', 'Domain Verification'].map((tag) => (
              <Chip
                key={tag}
                label={tag}
                onClick={() => { setSearchQuery(tag); setIsFocused(false); navigate(`/documentation?section=${categoryToSection[tag] ?? 'getting-started'}`); }}
                sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.65)', fontSize: '0.75rem', cursor: 'pointer', border: '1px solid rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.14)' } }}
              />
            ))}
          </Box>
        </Box>
      </Box>

      {/* â”€â”€ QUICK RESOURCES â”€â”€ */}
      <Box sx={{ bgcolor: '#0f172a', py: 5, px: { xs: 2, md: 5 }, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
          <Grid container spacing={3} justifyContent="center">
            {resources.map((r) => (
              <Grid size={{ xs: 12, sm: 4 }} key={r.title}>
                <Box
                  onClick={() => navigate(r.path)}
                  sx={{
                    p: 3, borderRadius: 3, cursor: 'pointer',
                    bgcolor: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.07)',
                    display: 'flex', flexDirection: 'column', gap: 1.5,
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: 'rgba(220,38,38,0.07)', borderColor: 'rgba(220,38,38,0.3)', transform: 'translateY(-2px)' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ p: 1.2, borderRadius: 2, bgcolor: 'rgba(220,38,38,0.12)', display: 'flex' }}>{r.icon}</Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#fff' }}>{r.title}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.7 }}>{r.desc}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#fca5a5' }}>
                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 600 }}>{r.cta}</Typography>
                    <ArrowForward sx={{ fontSize: 13 }} />
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* â”€â”€ CATEGORIES â”€â”€ */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>Knowledge Base</Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.8rem', md: '2.2rem' } }}>Browse by Category</Typography>
          </Box>
          <Grid container spacing={3}>
            {categories.map((c) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={c.title}>
                <Card elevation={0} onClick={() => navigate(`/documentation?section=${categoryToSection[c.title] ?? 'getting-started'}`)} sx={{
                  height: '100%', borderRadius: 3, border: '1px solid #e2e8f0', cursor: 'pointer',
                  transition: 'all 0.25s', '&:hover': { borderColor: '#fecaca', transform: 'translateY(-3px)', boxShadow: '0 8px 30px rgba(220,38,38,0.08)' },
                }}>
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fef2f2', display: 'inline-flex', mb: 2 }}>{c.icon}</Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a', mb: 0.5 }}>{c.title}</Typography>
                    <Typography sx={{ fontSize: '0.825rem', color: '#64748b', lineHeight: 1.6, mb: 1.5 }}>{c.desc}</Typography>
                    <Chip label={`${c.count} articles`} size="small" sx={{ bgcolor: '#f8fafc', color: '#64748b', fontSize: '0.7rem', fontWeight: 600 }} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* â”€â”€ CTA â”€â”€ */}
      <Box sx={{ py: { xs: 8, md: 10 }, px: { xs: 2, md: 5 }, background: 'linear-gradient(135deg, #dc2626 0%, #7f1d1d 100%)', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: -100, left: '50%', transform: 'translateX(-50%)', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.4rem' }, color: '#fff', mb: 2 }}>Still Need Help?</Typography>
          <Typography sx={{ fontSize: '1.05rem', color: 'rgba(255,255,255,0.8)', maxWidth: 480, mx: 'auto', mb: 4 }}>
            Our support team responds within hours. Send us a message and we'll take it from there.
          </Typography>
          <Button onClick={() => navigate('/contact')} endIcon={<ArrowForward />} sx={{ px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', bgcolor: '#fff', color: '#dc2626', '&:hover': { bgcolor: '#fef2f2' } }}>
            Contact Support
          </Button>
        </Box>
      </Box>

    </PublicLayout>
  );
};

export default HelpCenterPage;
